import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";

import {
  Button,
  ButtonBase,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  CircularProgress,
  Collapse,
  InputAdornment,
  TextField,
  Typography,
} from "@material-ui/core";
import { makeStyles, alpha } from "@material-ui/core/styles";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import ErrorOutlineIcon from "@material-ui/icons/ErrorOutline";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import LogoLoader from "../LogoLoader";
import { INTEGRATION_TYPES, SELECTABLE_TYPES } from "../../pages/QueueIntegration/integrationTypes";

const useStyles = makeStyles((theme) => ({
  paper: { borderRadius: 16 },
  title: { paddingBottom: 4, "& h2": { fontWeight: 600 } },
  content: { display: "flex", flexDirection: "column", gap: theme.spacing(2), paddingTop: theme.spacing(1) },
  label: { fontSize: 13, fontWeight: 600, marginBottom: theme.spacing(0.75), color: theme.palette.text.primary },
  typeGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
    gap: theme.spacing(1),
    [theme.breakpoints.down("xs")]: { gridTemplateColumns: "1fr" },
  },
  typeCard: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    textAlign: "left",
    gap: 6,
    padding: theme.spacing(1.25),
    borderRadius: 10,
    border: `1.5px solid ${theme.palette.divider}`,
    color: theme.palette.text.primary,
    "&:focus-visible": { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: 2 },
    [theme.breakpoints.down("xs")]: { flexDirection: "row", alignItems: "center", gap: 10 },
  },
  typeCardActive: {
    borderColor: theme.palette.primary.main,
    backgroundColor: alpha(theme.palette.primary.main, 0.08),
  },
  badge: {
    width: 28,
    height: 28,
    borderRadius: 7,
    flexShrink: 0,
    display: "grid",
    placeItems: "center",
    color: "#fff",
    fontWeight: 700,
    fontSize: 11,
  },
  typeName: { fontWeight: 600, fontSize: 14 },
  typeDesc: { fontSize: 12, lineHeight: 1.35, color: theme.palette.text.secondary },
  fields: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: theme.spacing(0.5, 1.5),
    [theme.breakpoints.down("xs")]: { gridTemplateColumns: "1fr" },
  },
  full: { gridColumn: "1 / -1" },
  field: { "& .MuiOutlinedInput-root": { borderRadius: 10 } },
  advancedToggle: {
    textTransform: "none",
    fontWeight: 600,
    color: theme.palette.text.secondary,
  },
  expand: { transition: "transform .15s" },
  expandOpen: { transform: "rotate(180deg)" },
  callout: {
    display: "flex",
    gap: theme.spacing(1.25),
    alignItems: "flex-start",
    padding: theme.spacing(1.25, 1.5),
    marginBottom: theme.spacing(1),
    borderRadius: 10,
    fontSize: 13,
    lineHeight: 1.45,
    backgroundColor: alpha(theme.palette.primary.main, 0.08),
    color: theme.palette.text.primary,
  },
  testRow: { display: "flex", alignItems: "center", gap: theme.spacing(1.25), flexWrap: "wrap" },
  testResult: { display: "flex", alignItems: "center", gap: 6, fontSize: 13 },
  ok: { color: theme.palette.success.main },
  fail: { color: theme.palette.error.main },
  actions: { padding: theme.spacing(1.5, 3, 2.5), gap: theme.spacing(1), flexWrap: "wrap" },
  button: { textTransform: "none", fontWeight: 600, borderRadius: 10 },
}));

const emptyValues = {
  type: "typebot",
  name: "",
  urlN8N: "",
  typebotSlug: "",
  typebotKeywordFinish: "",
  typebotExpires: 30,
  typebotDelaySeconds: 1,
  typebotKeywordRestart: "",
  typebotRestartMessage: "",
  typebotUnknownMessage: "",
};

const toFormValues = (data) => ({
  ...emptyValues,
  type: data.type,
  name: data.name || "",
  urlN8N: data.urlN8N || "",
  typebotSlug: data.typebotSlug || "",
  typebotKeywordFinish: data.typebotKeywordFinish || "",
  typebotExpires: data.typebotExpires != null ? data.typebotExpires : 0,
  // o banco guarda milissegundos; a tela mostra segundos
  typebotDelaySeconds: data.typebotDelayMessage != null ? data.typebotDelayMessage / 1000 : 1,
  typebotKeywordRestart: data.typebotKeywordRestart || "",
  typebotRestartMessage: data.typebotRestartMessage || "",
  typebotUnknownMessage: data.typebotUnknownMessage || "",
});

const toPayload = (v) => ({
  type: v.type,
  name: v.name.trim(),
  projectName: v.name.trim(),
  urlN8N: v.urlN8N.trim(),
  typebotSlug: v.typebotSlug.trim(),
  typebotKeywordFinish: v.typebotKeywordFinish,
  typebotExpires: Number(v.typebotExpires) || 0,
  typebotDelayMessage: Math.round(Number(v.typebotDelaySeconds) * 1000) || 0,
  typebotKeywordRestart: v.typebotKeywordRestart,
  typebotRestartMessage: v.typebotRestartMessage,
  typebotUnknownMessage: v.typebotUnknownMessage,
});

const IntegrationSchema = Yup.object().shape({
  name: Yup.string()
    .trim()
    .min(2, "Use pelo menos 2 letras.")
    .max(50, "Use até 50 caracteres.")
    .required("Dê um nome para a integração."),
  urlN8N: Yup.string()
    .trim()
    .matches(/^https?:\/\/\S+$/i, "Comece com https:// (ou http://).")
    .required("Informe o endereço."),
  typebotSlug: Yup.string().when("type", {
    is: "typebot",
    then: Yup.string()
      .trim()
      .matches(/^[\w-]+$/, "Use só letras, números e hífen.")
      .required("Informe o identificador do bot."),
  }),
  typebotExpires: Yup.number().typeError("Use um número.").integer("Use um número inteiro.").min(0, "Use 0 ou mais."),
  typebotDelaySeconds: Yup.number()
    .typeError("Use um número.")
    .min(0, "Use 0 ou mais.")
    .max(60, "No máximo 60 segundos."),
});

const QueueIntegrationModal = ({ open, onClose, integrationId }) => {
  const classes = useStyles();
  const history = useHistory();

  const [initialValues, setInitialValues] = useState(emptyValues);
  const [loadingData, setLoadingData] = useState(false);
  const [advanced, setAdvanced] = useState(false);
  const [test, setTest] = useState(null);
  const [created, setCreated] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    setTest(null);
    setCreated(false);
    setAdvanced(false);
    if (!integrationId) {
      setInitialValues(emptyValues);
      return undefined;
    }
    let cancelled = false;
    setLoadingData(true);
    api
      .get(`/queueIntegration/${integrationId}`)
      .then(({ data }) => {
        if (!cancelled) setInitialValues(toFormValues(data));
      })
      .catch(toastError)
      .finally(() => {
        if (!cancelled) setLoadingData(false);
      });
    return () => {
      cancelled = true;
    };
  }, [integrationId, open]);

  const handleTest = async (values) => {
    setTest({ loading: true });
    try {
      const { data } = await api.post("/queueIntegration/test", {
        type: values.type,
        urlN8N: values.urlN8N.trim(),
        typebotSlug: values.typebotSlug.trim(),
      });
      setTest({ result: data });
    } catch (err) {
      setTest(null);
      toastError(err);
    }
  };

  const handleSave = async (values) => {
    try {
      if (integrationId) {
        await api.put(`/queueIntegration/${integrationId}`, toPayload(values));
        toast.success("Integração salva.");
        onClose();
      } else {
        await api.post("/queueIntegration", toPayload(values));
        toast.success("Integração criada.");
        setCreated(true);
      }
    } catch (err) {
      // não fecha: o usuário corrige e tenta de novo
      toastError(err);
    }
  };

  const fieldProps = (name, touched, errors, extra = {}) => ({
    as: TextField,
    id: `integration-${name}`,
    name,
    variant: "outlined",
    size: "small",
    margin: "dense",
    fullWidth: true,
    className: classes.field,
    error: Boolean(touched[name] && errors[name]),
    helperText: (touched[name] && errors[name]) || extra.hint || " ",
    ...extra.props,
  });

  if (created) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth classes={{ paper: classes.paper }}>
        <DialogTitle className={classes.title}>Integração criada</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary">
            Ela só começa a funcionar quando você escolhe ela numa fila, em <b>Filas › editar › Integração</b>.
          </Typography>
        </DialogContent>
        <DialogActions className={classes.actions}>
          <Button onClick={onClose} variant="outlined" className={classes.button}>
            Agora não
          </Button>
          <Button
            variant="contained"
            color="primary"
            disableElevation
            className={classes.button}
            onClick={() => {
              onClose();
              history.push("/queues");
            }}
          >
            Ligar a uma fila
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth scroll="paper" classes={{ paper: classes.paper }}>
      {loadingData && <LogoLoader overlay />}
      <DialogTitle className={classes.title}>{integrationId ? "Editar integração" : "Nova integração"}</DialogTitle>
      <Formik
        initialValues={initialValues}
        enableReinitialize
        validationSchema={IntegrationSchema}
        onSubmit={async (values) => {
          // Aguarda a requisição: o Formik mantém isSubmitting=true e o botão
          // desabilitado, evitando envio duplicado.
          await handleSave(values);
        }}
      >
        {({ touched, errors, isSubmitting, values, setFieldValue, validateForm, setTouched }) => {
          const isTypebot = values.type === "typebot";
          const runTest = async () => {
            const errs = await validateForm();
            const relevant = ["urlN8N", ...(isTypebot ? ["typebotSlug"] : [])];
            if (relevant.some((k) => errs[k])) {
              setTouched({ ...touched, urlN8N: true, typebotSlug: true });
              return;
            }
            handleTest(values);
          };

          return (
            <Form>
              <DialogContent dividers className={classes.content}>
                <div>
                  <div className={classes.label} id="integration-type-label">
                    O que você quer conectar?
                  </div>
                  <div className={classes.typeGrid} role="radiogroup" aria-labelledby="integration-type-label">
                    {SELECTABLE_TYPES.map((key) => {
                      const info = INTEGRATION_TYPES[key];
                      const active = values.type === key;
                      return (
                        <ButtonBase
                          key={key}
                          role="radio"
                          aria-checked={active}
                          className={`${classes.typeCard} ${active ? classes.typeCardActive : ""}`}
                          onClick={() => {
                            setFieldValue("type", key);
                            setTest(null);
                          }}
                        >
                          <span className={classes.badge} style={{ backgroundColor: info.color }} aria-hidden="true">
                            {info.short}
                          </span>
                          <span>
                            <span className={classes.typeName} style={{ display: "block" }}>
                              {info.label}
                            </span>
                            <span className={classes.typeDesc} style={{ display: "block" }}>
                              {info.description}
                            </span>
                          </span>
                        </ButtonBase>
                      );
                    })}
                  </div>
                </div>

                <div className={classes.fields}>
                  <div className={classes.full}>
                    <Field
                      {...fieldProps("name", touched, errors, {
                        hint: "Aparece na lista de integrações e no cadastro da fila.",
                        props: { label: "Nome", autoFocus: !integrationId },
                      })}
                    />
                  </div>

                  {isTypebot ? (
                    <>
                      <Field
                        {...fieldProps("urlN8N", touched, errors, {
                          hint: "Só o endereço principal, sem /api. Ex.: https://typebot.io",
                          props: { label: "Endereço do Typebot" },
                        })}
                      />
                      <Field
                        {...fieldProps("typebotSlug", touched, errors, {
                          hint: "A última parte do link de compartilhar do bot.",
                          props: { label: "Identificador do bot" },
                        })}
                      />
                      <Field
                        {...fieldProps("typebotKeywordFinish", touched, errors, {
                          hint: "O cliente digita isso para falar com uma pessoa.",
                          props: { label: "Palavra para sair do bot" },
                        })}
                      />
                      <Field
                        {...fieldProps("typebotExpires", touched, errors, {
                          hint: "Tempo parado até a conversa recomeçar. 0 = nunca.",
                          props: {
                            label: "Reiniciar conversa após",
                            type: "number",
                            inputProps: { min: 0, step: 1 },
                            InputProps: { endAdornment: <InputAdornment position="end">min</InputAdornment> },
                          },
                        })}
                      />
                    </>
                  ) : (
                    <>
                      <div className={classes.full}>
                        <Field
                          {...fieldProps("urlN8N", touched, errors, {
                            hint: "Endereços internos do servidor (localhost, rede local) são recusados.",
                            props: {
                              label:
                                values.type === "n8n" ? "URL do webhook do n8n" : "URL que vai receber as mensagens",
                            },
                          })}
                        />
                      </div>
                      <div className={`${classes.callout} ${classes.full}`}>
                        <InfoOutlinedIcon fontSize="small" style={{ marginTop: 1 }} />
                        <span>
                          Cada mensagem recebida na fila é enviada para essa URL. Esta integração só recebe uma
                          cópia: ela não responde o cliente.
                        </span>
                      </div>
                    </>
                  )}
                </div>

                {isTypebot && (
                  <div>
                    <Button
                      size="small"
                      className={classes.advancedToggle}
                      onClick={() => setAdvanced((a) => !a)}
                      aria-expanded={advanced}
                      endIcon={
                        <ExpandMoreIcon className={`${classes.expand} ${advanced ? classes.expandOpen : ""}`} />
                      }
                    >
                      Opções avançadas
                    </Button>
                    <Collapse in={advanced}>
                      <div className={classes.fields} style={{ marginTop: 8 }}>
                        <Field
                          {...fieldProps("typebotDelaySeconds", touched, errors, {
                            hint: "Espera entre uma mensagem do bot e outra.",
                            props: {
                              label: "Pausa entre mensagens",
                              type: "number",
                              inputProps: { min: 0, max: 60, step: 0.5 },
                              InputProps: { endAdornment: <InputAdornment position="end">s</InputAdornment> },
                            },
                          })}
                        />
                        <Field
                          {...fieldProps("typebotKeywordRestart", touched, errors, {
                            hint: "O cliente digita isso para começar de novo.",
                            props: { label: "Palavra para recomeçar" },
                          })}
                        />
                        <div className={classes.full}>
                          <Field
                            {...fieldProps("typebotRestartMessage", touched, errors, {
                              props: { label: "Mensagem ao recomeçar" },
                            })}
                          />
                        </div>
                        <div className={classes.full}>
                          <Field
                            {...fieldProps("typebotUnknownMessage", touched, errors, {
                              props: { label: "Mensagem quando o bot não entende" },
                            })}
                          />
                        </div>
                      </div>
                    </Collapse>
                  </div>
                )}

                <div className={classes.testRow}>
                  <Button
                    variant="outlined"
                    size="small"
                    className={classes.button}
                    onClick={runTest}
                    disabled={Boolean(test && test.loading) || isSubmitting}
                  >
                    Testar conexão
                  </Button>
                  {test && test.loading && (
                    <span className={classes.testResult}>
                      <CircularProgress size={14} /> Testando…
                    </span>
                  )}
                  {test && test.result && (
                    <span
                      role="status"
                      className={`${classes.testResult} ${test.result.ok ? classes.ok : classes.fail}`}
                    >
                      {test.result.ok ? (
                        <CheckCircleIcon style={{ fontSize: 16 }} />
                      ) : (
                        <ErrorOutlineIcon style={{ fontSize: 16 }} />
                      )}
                      {test.result.message}
                    </span>
                  )}
                  {!test && (
                    <Typography variant="caption" color="textSecondary">
                      {isTypebot
                        ? "Inicia uma conversa de teste no bot."
                        : "Envia um evento de teste (event: messageexpress.test)."}
                    </Typography>
                  )}
                </div>
              </DialogContent>

              <DialogActions className={classes.actions}>
                <Button onClick={onClose} disabled={isSubmitting} variant="outlined" className={classes.button}>
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  color="primary"
                  variant="contained"
                  disableElevation
                  disabled={isSubmitting}
                  className={classes.button}
                >
                  {isSubmitting ? (
                    <CircularProgress size={18} color="inherit" />
                  ) : integrationId ? (
                    "Salvar"
                  ) : (
                    "Criar integração"
                  )}
                </Button>
              </DialogActions>
            </Form>
          );
        }}
      </Formik>
    </Dialog>
  );
};

export default QueueIntegrationModal;
